import { Component, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { BusyIndicatorComponent } from "@tylertech/common-components-module";
import { UserProfileService } from "@tylertech/core-module";
import { ForgeCardModule, ForgeToolbarModule } from "@tylertech/forge-angular";
import { InfoCardComponent, InfoCardService } from "@tylertech/info-card-module";
import { LgdFormControlFactory, LgdTypeAheadComponent } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor } from "@tylertech/logging";
import { CachedCodeLookupService } from "@tylertech/metadata-module";
import { ODataFilterBuilder } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { IInfoCardData, KeyValue } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable } from "rxjs";

const enum UserProviders
{
    UserLookup = "UserLookup.InfoCardProvider",
    UserEquipment = "UserEquipment.InfoCardProvider",
}

@Component({
    selector: "user-lookup-card",
    imports: [
        BusyIndicatorComponent,
        ForgeCardModule,
        ForgeToolbarModule,
        ReactiveFormsModule,
        InfoCardComponent,
        LgdTypeAheadComponent,
    ],
    templateUrl: "./userLookup.tmpl.html",
    styleUrls: [
        "./userLookup.component.scss"
    ],
})
export class UserLookupCardComponent
{
    private readonly $userProfileService = inject(UserProfileService);
    private readonly $cachedCodeLookupService = inject(CachedCodeLookupService);
    private readonly $infoCardService = inject(InfoCardService);

    private readonly log = getLoggerFor("UserLookupCardComponent");

    private readonly userLookupSubject = new BehaviorSubject<Array<KeyValue>>([]);
    readonly userLookupObservable: Observable<Array<KeyValue>>;

    private readonly lookupType = signal<string | null>(null);

    readonly userLookupForm: FormGroup;

    readonly showCard = signal<boolean>(false);
    readonly loading = signal<boolean>(false);
    readonly selectedUserId = signal<string | null>(null);

    userCard: IInfoCardData;
    userEquipmentCard: IInfoCardData;

    constructor()
    {
        this.userLookupObservable = this.userLookupSubject.asObservable();;
        this.userLookupForm = new FormGroup({
            user: LgdFormControlFactory.typeahead<KeyValue>(
                null,
                false,
                {
                    displayValueFn: x => x ? x.Value : "",
                }),
        });

        this.userLookupFormControl.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(x =>
            {
                this.selectedUserId.set(x.Key);

                this.load(this.selectedUserId());
            });

        this.$userProfileService.GetUserLookupType()
            .then(x =>
            {
                if (!x)
                {
                    return;
                }

                this.lookupType.set(x);
                this.showCard.set(true);
                this.lookupUser("");
            });
    }

    private get userLookupFormControl(): AbstractControl<KeyValue> { return this.userLookupForm.get("user") as AbstractControl<KeyValue>; }

    lookupUser(searchText: string): void
    {
        this.getUsers(searchText)
            .then(results =>
            {
                if (results.length === 1)
                {
                    this.userLookupFormControl.setValue(results[0].Key);
                }

                this.userLookupSubject.next(results);
            });
    }

    private getUsers(searchText: string): Promise<Array<KeyValue>>
    {
        const filterString = new ODataFilterBuilder<KeyValue>()
            .like("Value", Utilities.toLower(searchText))
            .build();
        if (!filterString)
        {
            return Promise.resolve([]);
        }

        return this.$cachedCodeLookupService.Get(this.lookupType(), filterString)
            .catch((error) =>
            {
                this.log.error(
                    `getUsers|lookupType: ${JSON.stringify(this.lookupType)}|filter: ${JSON.stringify(filterString)}`,
                    { error });

                return [];
            });
    }

    private load(id: string): Promise<void>
    {
        this.loading.set(true);
        return Promise.all([
            this.$infoCardService.Get({
                Id: id,
                CardProviderId: UserProviders.UserLookup,
                Criteria: {},
            })
                .then(
                    (item: IInfoCardData) =>
                    {
                        this.userCard = item;
                    },
                    error =>
                    {
                        this.log.error(`Could not fetch user infoCard`, { error });
                    }),
            this.$infoCardService.Get({
                Id: id,
                CardProviderId: UserProviders.UserEquipment,
                Criteria: {},
            })
                .then(
                    (item: IInfoCardData) =>
                    {
                        this.userEquipmentCard = item;
                    },
                    error =>
                    {
                        this.log.error(`Could not fetch user equipment infoCard`, { error });
                    }),
        ])
            .then(_ =>
            {
                /* Suppressing empty results array */
            })
            .finally(() =>
            {
                this.loading.set(false);
            });
    }
}
